import type { CustomPricingSort, ListResponseCustomPricing, SortOrder } from "@classes-service-types/index.ts";
import type { UseQueryOptions } from "@tanstack/vue-query";
import { useQuery } from "@tanstack/vue-query";
import type { AxiosResponse } from "axios";
import { computed, type Ref, unref } from "vue";
import { learningServiceRequest } from "@/services/utils";

export const QUERY_KEY = "getCustomPricingList";

export const getQueryKey = (params: Record<string, unknown>) => [QUERY_KEY, params] as const;

export const useGetCustomPricingList = <TData = ListResponseCustomPricing>(
  limit?: Ref<number> | number,
  skip?: Ref<number> | number,
  field?: Ref<CustomPricingSort> | CustomPricingSort,
  order?: Ref<SortOrder> | SortOrder,
  q?: Ref<string | undefined>,
  sessionId?: Ref<string | undefined>,
  homeInstitutionId?: Ref<string | undefined>,
  statusId?: Ref<number | undefined>,
  ownerEntityId?: Ref<string | undefined>,
  channelId?: Ref<string | undefined>,
  cityId?: Ref<number | undefined>,
  hostInstitutionIds?: Ref<string[] | undefined>,
  termIds?: Ref<string[] | undefined>,
  tagIds?: Ref<string[] | undefined>,
  overrideId?: Ref<string | undefined>,
  titleQ?: Ref<string | undefined>,
  sessionNameQ?: Ref<string | undefined>,
  homeInstitutionNameQ?: Ref<string | undefined>,
  updatedByQ?: Ref<string | undefined>,
  useQueryOptions?: Partial<UseQueryOptions<ListResponseCustomPricing, Error, TData>>,
) => {
  const requestParams = computed(() => ({
    limit: unref(limit),
    skip: unref(skip),
    field: unref(field),
    order: unref(order),
    q: unref(q),
    session_id: unref(sessionId),
    home_institution_id: unref(homeInstitutionId),
    status_id: unref(statusId),
    owner_entity_id: unref(ownerEntityId),
    channel_id: unref(channelId),
    city_id: unref(cityId),
    host_institution_ids: unref(hostInstitutionIds),
    term_ids: unref(termIds),
    tag_ids: unref(tagIds),
    override_id: unref(overrideId),
    title_q: unref(titleQ),
    session_name_q: unref(sessionNameQ),
    home_institution_name_q: unref(homeInstitutionNameQ),
    updated_by_q: unref(updatedByQ),
  }));

  const fetchCustomPricingList = (): Promise<ListResponseCustomPricing> =>
    learningServiceRequest({
      method: "get",
      url: "/custom_pricing/",
      params: requestParams.value,
    }).then((response: AxiosResponse<ListResponseCustomPricing>) => response.data);

  return useQuery<ListResponseCustomPricing, Error, TData>({
    queryKey: computed(() => getQueryKey(requestParams.value)),
    queryFn: fetchCustomPricingList,
    ...useQueryOptions,
  });
};
